import React, { FormEvent, useEffect, useId, useRef, useState } from "react";
import { CalendarClock, Check, Clock3, PhoneCall, RefreshCw } from "lucide-react";
import { Link } from "wouter";
import { createConversionAttribution } from "@/lib/attribution";
import { trackGa4Lead } from "@/lib/ga4";
import { trpc } from "@/lib/trpc";
import { siteConfig } from "@/data/site";

type CallbackSourcePath = "/contact-partition-stalls" | "/request-a-quote";

type CallbackSchedulerProps = {
  sourcePath: CallbackSourcePath;
  headingId: string;
  variant?: "contact" | "quote";
  showQuoteLink?: boolean;
  showHeading?: boolean;
};

const callbackThankYouGraphic = "/manus-storage/partition-stalls-callback-thank-you_1a049106.png";

type CallbackThankYouProps = {
  confirmation: { dateLabel: string; rangeLabel: string; phone: string };
  onScheduleAnother: () => void;
  focusRef?: React.RefObject<HTMLDivElement | null>;
};

export function CallbackThankYou({ confirmation, onScheduleAnother, focusRef }: CallbackThankYouProps) {
  return (
    <div className="callback-thanks" role="status" aria-live="polite" tabIndex={-1} ref={focusRef}>
      <div className="callback-thanks__graphic" aria-hidden="true">
        <span className="callback-thanks__check"><Check /></span>
        <img src={callbackThankYouGraphic} alt="" width="720" height="790" />
      </div>
      <div className="callback-thanks__content">
        <span className="callback-thanks__kicker"><Check aria-hidden="true" /> Booking confirmed</span>
        <h3>Thank you—your callback is confirmed.</h3>
        <p className="callback-thanks__lead">We’ll call you at the time below.</p>
        <dl className="callback-thanks__details">
          <div><dt>Date</dt><dd>{confirmation.dateLabel}</dd></div>
          <div><dt>Time</dt><dd>{confirmation.rangeLabel} Eastern Time</dd></div>
          <div><dt>Number we’ll call</dt><dd>{confirmation.phone}</dd></div>
        </dl>
        <p className="callback-thanks__next">Have plans, measurements, photos, or product questions? Keep them nearby so we can help quickly.</p>
        <div className="callback-thanks__actions">
          <a className="button button--primary" href={`tel:${siteConfig.phoneHref}`}><PhoneCall aria-hidden="true" /> Call {siteConfig.phoneDisplay}</a>
          <button type="button" className="button callback-thanks__again" onClick={onScheduleAnother}>Schedule another callback</button>
        </div>
      </div>
    </div>
  );
}

export function CallbackScheduler({
  sourcePath,
  headingId,
  variant = "contact",
  showQuoteLink = false,
  showHeading = true,
}: CallbackSchedulerProps) {
  const formId = useId();
  const utils = trpc.useUtils();
  const availability = trpc.callbacks.availability.useQuery(undefined, {
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
  });
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedStart, setSelectedStart] = useState("");
  const [company, setCompany] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [confirmation, setConfirmation] = useState<{ dateLabel: string; rangeLabel: string; phone: string } | null>(null);
  const confirmationRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const timeSelectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (confirmed) confirmationRef.current?.focus({ preventScroll: true });
  }, [confirmed]);

  useEffect(() => {
    const days = availability.data?.days ?? [];
    if (!days.length) {
      setSelectedDate("");
      setSelectedStart("");
      return;
    }
    const currentDay = days.find((day) => day.businessDate === selectedDate);
    if (!currentDay) {
      const firstAvailableDay = days.find((day) => day.slots.length > 0) ?? days[0];
      setSelectedDate(firstAvailableDay.businessDate);
      setSelectedStart("");
      return;
    }
    if (selectedStart && !currentDay.slots.some((slot) => slot.startAt === selectedStart)) {
      setSelectedStart("");
    }
  }, [availability.data?.days, selectedDate, selectedStart]);

  const booking = trpc.callbacks.book.useMutation({
    onSuccess: async (result) => {
      if (!result.bookingId) {
        setNotice("Your request could not be confirmed. Please call our team directly.");
        return;
      }
      setConfirmed(true);
      setConfirmation({ dateLabel: result.dateLabel, rangeLabel: result.slot.rangeLabel, phone });
      trackGa4Lead("callback_request");
      setNotice("");
      setSelectedStart("");
      await utils.callbacks.availability.invalidate();
    },
    onError: async (error) => {
      setConfirmed(false);
      setConfirmation(null);
      setNotice(error.message);
      await utils.callbacks.availability.invalidate();
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setConfirmed(false);
    setConfirmation(null);
    setNotice("");
    if (!selectedStart) {
      setNotice("Choose an available callback time.");
      window.requestAnimationFrame(() => timeSelectRef.current?.focus());
      return;
    }
    if (!event.currentTarget.checkValidity()) {
      event.currentTarget.reportValidity();
      return;
    }
    booking.mutate({
      ...createConversionAttribution(sourcePath),
      name,
      phone,
      startAt: selectedStart,
      sourcePath,
      company,
    });
  };

  const days = availability.data?.days ?? [];
  const fallbackDay = days.find((day) => day.slots.length > 0) ?? days[0];
  const activeDate = selectedDate || fallbackDay?.businessDate || "";
  const selectedDay = days.find((day) => day.businessDate === activeDate);
  const slots = selectedDay?.slots ?? [];
  const lastDay = days.at(-1);
  const unavailableCopy = selectedDay
    ? `${selectedDay.dateLabel} has no open callback times. Choose another date.`
    : "Callback availability is temporarily unavailable.";
  const scheduleAnother = () => {
    setConfirmed(false);
    setConfirmation(null);
    setNotice("");
    setSelectedStart("");
    window.requestAnimationFrame(() => nameInputRef.current?.focus());
  };

  return (
    <section className={`callback-scheduler callback-scheduler--${variant}${showHeading ? "" : " callback-scheduler--headingless"}`} aria-labelledby={headingId}>
      {showHeading && (
        <div className="callback-scheduler__heading">
          <span className="callback-scheduler__kicker"><PhoneCall aria-hidden="true" /> Scheduled callback</span>
          <h2 id={headingId}>Choose a callback time.</h2>
          <p>Pick a day and a 30-minute window. We’ll call the number you provide.</p>
        </div>
      )}

      {confirmed && confirmation ? (
        <CallbackThankYou confirmation={confirmation} onScheduleAnother={scheduleAnother} focusRef={confirmationRef} />
      ) : (
        <form className="callback-form" onSubmit={handleSubmit} noValidate>
          <div className="callback-form__date" aria-live="polite">
            <CalendarClock aria-hidden="true" />
            <span>{availability.data && lastDay ? `Through ${lastDay.dateLabel} · ${availability.data.timeZoneLabel}` : "Checking Eastern Time availability…"}</span>
            <button type="button" onClick={() => availability.refetch()} aria-label="Refresh callback times" disabled={availability.isFetching}>
              <RefreshCw aria-hidden="true" className={availability.isFetching ? "is-spinning" : ""} />
            </button>
          </div>

          <div className="callback-field">
            <label htmlFor={`${formId}-name`}>Name</label>
            <input ref={nameInputRef} id={`${formId}-name`} name="name" value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" minLength={2} maxLength={120} required />
          </div>
          <div className="callback-field">
            <label htmlFor={`${formId}-phone`}>Phone</label>
            <input id={`${formId}-phone`} name="phone" type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} autoComplete="tel" inputMode="tel" minLength={7} maxLength={64} placeholder="(646) 349-4040" required />
          </div>
          <div className="callback-field callback-field--date">
            <label htmlFor={`${formId}-date`}>Date</label>
            <div className="callback-field__select">
              <CalendarClock aria-hidden="true" />
              <select id={`${formId}-date`} name="businessDate" value={activeDate} onChange={(event) => { setSelectedDate(event.target.value); setSelectedStart(""); }} disabled={availability.isLoading || availability.isError || days.length === 0} required>
                <option value="" disabled>{availability.isLoading ? "Checking available dates…" : "Choose a business day"}</option>
                {days.map((day) => <option key={day.businessDate} value={day.businessDate}>{day.dateLabel}{day.isToday ? " — Today" : ""}</option>)}
              </select>
            </div>
          </div>
          <div className="callback-field callback-field--time">
            <label htmlFor={`${formId}-time`}>Time (30 minutes)</label>
            <div className="callback-field__select">
              <Clock3 aria-hidden="true" />
              <select ref={timeSelectRef} id={`${formId}-time`} name="startAt" value={selectedStart} onChange={(event) => { setSelectedStart(event.target.value); if (event.target.value) setNotice(""); }} disabled={availability.isLoading || availability.isError || !selectedDay || slots.length === 0} required>
                <option value="" disabled>{availability.isLoading ? "Checking available times…" : slots.length ? "Choose a 30-minute callback" : "No open times on this date"}</option>
                {slots.map((slot) => <option key={slot.startAt} value={slot.startAt}>{slot.rangeLabel}</option>)}
              </select>
            </div>
          </div>
          <div className="callback-field callback-field--honeypot" aria-hidden="true">
            <label htmlFor={`${formId}-company`}>Company website</label>
            <input id={`${formId}-company`} name="callback_check" value={company} onChange={(event) => setCompany(event.target.value)} tabIndex={-1} autoComplete="new-password" />
          </div>

          {availability.isError && <p className="callback-form__notice" role="status">Callback times are temporarily unavailable. Please call us directly.</p>}
          {!availability.isLoading && !availability.isError && selectedDay && slots.length === 0 && <p className="callback-form__notice" role="status">{unavailableCopy}</p>}
          {!availability.isLoading && !availability.isError && days.length === 0 && <p className="callback-form__notice" role="status">{unavailableCopy} <a href={`tel:${siteConfig.phoneHref}`}>Call {siteConfig.phoneDisplay}</a>.</p>}
          {notice && !confirmed && <p className="callback-form__notice callback-form__notice--error" role="status" aria-live="polite">{notice}</p>}

          <button type="submit" className="button button--primary callback-form__submit" disabled={booking.isPending || availability.isLoading || availability.isError || days.length === 0}>
            {booking.isPending ? "Reserving…" : "Reserve callback"}
          </button>
          <p className="callback-form__consent">We’ll call this number at the selected time.</p>
        </form>
      )}

      {!confirmed && (
        <div className="callback-scheduler__alternates">
          <a href={`tel:${siteConfig.phoneHref}`}>Call now: {siteConfig.phoneDisplay}</a>
          {showQuoteLink && <Link href="/request-a-quote">Need pricing? Request a full quote</Link>}
        </div>
      )}
    </section>
  );
}
